<template>
    <div class="routingSettings">
        <b-card bg-variant="light" :title="$t('assistantLabel')">
            <b-form-group>
                <small>{{$t('assistantInfo')}}</small>
                <recipient-editor v-model="assistant" border :parent-validation="true"
                        :entry-valid="assistantEntryValid" @entry="assistantEntry=$event"
                        @input="assistantServerValidated=''" :invalidEntries="invalidAssistants"
                        :placeholder="$t('assistantPlaceholder')" :state="!$v.assistant.$invalid"
                ></recipient-editor>
                <b-form-invalid-feedback :state="!$v.assistant.$invalid"
                        v-html="feedback('assistant')"></b-form-invalid-feedback>
                <b-checkbox v-model="forwardToAssistant">{{$t('forwardToAssistantLabel')}}</b-checkbox>
            </b-form-group>
        </b-card>
        <b-card bg-variant="light" class="mt-3" :title="$t('forwardToLabel')">
            <b-form-group>
                <small>{{$t('forwardToInfo')}}</small>
                <recipient-editor v-model="forwardTo" border :parent-validation="true"
                        :entry-valid="forwardToEntryValid" @entry="forwardToEntry=$event"
                        @input="forwardToServerValidated=''" :invalidEntries="invalidForwards"
                        :placeholder="$t('forwardToPlaceholder')" :state="!$v.forwardTo.$invalid"
                ></recipient-editor>
                <b-form-invalid-feedback :state="!$v.forwardTo.$invalid"
                        v-html="feedback('forwardTo')"></b-form-invalid-feedback>
                <b-checkbox v-model="useForwards">{{$t('useForwardsLabel')}}</b-checkbox>
            </b-form-group>
        </b-card>

        <b-button :disabled="disableSave" variant="primary"
                @click="doSave">
            {{$t('save')}}
        </b-button>
        <font-awesome-icon :class="{ saveIcon: true, savingIcon: saving }" spin
                icon="spinner-third"></font-awesome-icon>
        <span class="error">{{saveError}}</span>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axios from 'axios';
import { EventBus } from '@/event-bus';
import RecipientEditor from '@/components/recipientEditor';

export default {
  name: 'routingSettings',
  components: { FontAwesomeIcon, RecipientEditor },
  i18nOptions: { keyPrefix: 'settings.routing' },
  data: function() {
    return {
      forwardTo: '',
      forwardToServerValidated: '',
      forwardToEntry: '',
      invalidForwards: [],
      useForwards: false,
      assistant: [],
      assistantServerValidated: '',
      assistantEntry: '',
      invalidAssistants: [],
      forwardToAssistant: false,
      saving: false,
      saveError: ''
    };
  },
  validations: {
    forwardTo: {
      serverValidateForwardTo(value, vm) {
        return !vm.forwardToServerValidated;
      }
    },
    assistant: {
      serverValidateAssistant(value, vm) {
        return !vm.assistantServerValidated;
      }
    }
  },
  created() {
    EventBus.$on('settingsLoaded', () => {
      this.initSettings();
    });
  },
  mounted() {
    this.initSettings();
  },
  watch: {
    async assistant(newVal) {
      if (newVal) {
        const r = await this.serverValidate('validateassistants',
          { mode: 'text', assistants: newVal });
        this.assistantServerValidated = r.result;
        this.invalidAssistants = r.errors;
      }
    },
    async forwardTo(newVal) {
      if (newVal) {
        const r = await this.serverValidate('validateforwards',
          { mode: 'text', forwards: newVal });
        this.forwardToServerValidated = r.result;
        this.invalidForwards = r.errors;
      }
    }
  },
  methods: {
    initSettings() {
      this.assistant = this.$store.state.settings.assistant;
      this.forwardToAssistant = this.$store.state.settings.forwardtoassistant;
      this.forwardTo = this.$store.state.settings.forwardto;
      this.useForwards = this.$store.state.settings.useforwards;
    },
    doSave() {
      if (!this.$store.state.settings.cansetforwards) {
        return;
      }
      let settings = {
        assistant: this.assistant,
        forwardtoassistant: this.forwardToAssistant,
        forwardto: this.forwardTo,
        useforwards: this.useForwards
        // alwaysshowimages: this.alwaysShowImages
      };
      this.saving = true;
      axios.patch('/rest/user/settings', settings)
        .then(d => {
          console.log(d);
          this.saveError = '';
        })
        .catch(e => {
          this.saveError = this.$t('saveError');
          let saveErrorExtra = [];
          for (let err in e.response.data.errorfields) {
            switch (err) {
              case 'assistant':
                this.assistantServerValidated = '<' + e.response.data.errorfields[err];
                break;
              case 'forwardto':
                this.forwardToServerValidated = '<' + e.response.data.errorfields[err];
                break;
              default:
                saveErrorExtra.push(this.$t(`saveErrors.${e.response.data.errorfields[err]}`, { field: err }));
                break;
            }
            if (saveErrorExtra.length > 0) {
              this.saveError = this.$t('saveErrorWithExtra', { extra: saveErrorExtra.join(', ') });
            } else {
              this.saveError = this.$t('saveError');
            }
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    serverValidate(rpcName, params) {
      return new Promise((resolve, reject) => {
        this.$RPC('settings', rpcName, params)
          .then(d => {
            console.log('server validated', d);
            resolve(d);
          })
          .catch(() => {
            resolve('');
          });
      });
    },
    feedback(field) {
      console.log('feedback', field);
      console.log(this['assistantServerValidated']);
      if (this.$v[field].required === false) {
        return this.$t('general:validation.required');
      } else if (this[`${field}ServerValidated`]) {
        const error = this[`${field}ServerValidated`];
        console.log('server Validation error', error);
        if (error === '.') {
          return this.$t('general:validation.server');
        } else if (error.substr(0, 1) === '<') {
          const error2 = this.$t(`saveErrors.${error.substr(1)}`, { field: this.$t('saveErrors.thisField') });
          return this.$t('general:validation.serverExtraInfo', { error: error2 });
        } else {
          return this.$t('general:validation.serverExtraInfo', { error });
        }
      }
      return '';
    },
    addressEntryValid(addresses) {
      let trimmed = addresses.trim();
      // Simple email address
      if (trimmed.substr(0, 5).toUpperCase() === 'SMTP:') {
        // SMTP Forward Email address
        const re2 = /^SMTP:([^@]+@[a-z0-9\\.-]+)@(\S+:\S+@)?[a-z0-9.-]+(:[0-9]+)?$/i;
        if (re2.exec(trimmed)) {
          console.log('SMTP Fwd match');
          return true;
        }
      } else {
        const re1 = /^[^\s@]+@[a-z0-9.-]+\.[a-z0-9-]{2,50}$/i;
        if (re1.exec(trimmed)) {
          console.log('simple match');
          return true;
        }
      }
      return false;
    }
  },
  computed: {
    disableSave() {
      return this.$v.$invalid || this.saving || !this.$store.state.settingsLoaded ||
        !this.$store.state.settings.cansetforwards;
    },
    allowed() {
      return this.$store.state.settings.cansetforwards;
    },
    forwardToEntryValid() {
      return this.addressEntryValid(this.forwardToEntry);
    },
    assistantEntryValid() {
      return this.addressEntryValid(this.assistantEntry);
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .saveIcon {
        color: transparent;
        margin: 0 5px;
    &.savingIcon {
         color: black;
     }
    }

    .error {
        color: $danger;
    }

</style>
